/* =====================================================================
   OGPB Storefront Auditor  (poopbags.com / Impact theme)
   ---------------------------------------------------------------------
   One client-side sweep that surfaces the things that actually cost
   money or trust on a live store:
     - pricing faults (zero, negative, broken or fake compare-at sales)
     - cart and discount math that does not add up
     - subscription selling-plan prices that did not apply
     - empty collections, product cards with no price or broken image
     - broken media, duplicate element IDs, runtime JS errors
     - the original performance read (slow, heavy, third-party drag)
   Output: a floating panel you can read at a glance plus a grouped
   console report (open DevTools console for the full detail).
   ---------------------------------------------------------------------
   Activation (invisible and weightless for real shoppers):
     ?audit         run once on this page view
     ?audit=stick   run, and keep running on every page in this browser
     ?audit=off     clear the sticky flag
     Theme editor (Shopify.designMode) turns it on automatically
   Manual re-run from the console: OGPBAudit.run()

   Money note: amounts from page data and /cart.js are in cents.
   parseMoney() assumes USD formatting (comma = thousands).
   ===================================================================== */
(function () {
  'use strict';

  if (window.__ogpbAuditLoaded) { return; }
  window.__ogpbAuditLoaded = true;

  /* --- catch runtime errors as early as possible -------------------- */
  var runtimeErrors = [];
  window.addEventListener('error', function (e) {
    runtimeErrors.push({
      message: e.message || 'Script error',
      where: (e.filename || '') + (e.lineno ? ':' + e.lineno : '')
    });
  }, true);
  window.addEventListener('unhandledrejection', function (e) {
    var reason = (e.reason && e.reason.message) ? e.reason.message : String(e.reason);
    runtimeErrors.push({ message: 'Unhandled promise rejection: ' + reason, where: '' });
  });

  /* --- activation gate ---------------------------------------------- */
  var params = new URLSearchParams(location.search);
  if (params.get('audit') === 'off') {
    try { localStorage.removeItem('ogpb_audit'); } catch (e) {}
    return;
  }
  if (params.get('audit') === 'stick') {
    try { localStorage.setItem('ogpb_audit', '1'); } catch (e) {}
  }
  var sticky = false;
  try { sticky = localStorage.getItem('ogpb_audit') === '1'; } catch (e) {}
  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  if (!params.has('audit') && !sticky && !inEditor) { return; }

  /* --- config -------------------------------------------------------- */
  var CFG = {
    slowMs: 200,
    criticalMs: 500,
    largeBytes: 100000,
    firstParty: [location.hostname, 'cdn.shopify.com', 'cdn.shopifycdn.com']
  };

  var SEVMETA = {
    critical: { color: '#ff4d4f', label: 'CRITICAL' },
    warning:  { color: '#f5a623', label: 'WARNING' },
    info:     { color: '#4aa3ff', label: 'INFO' }
  };

  /* --- issue collector ---------------------------------------------- */
  var issues = [];
  function flag(severity, area, title, detail, evidence) {
    issues.push({
      severity: severity,
      area: area,
      title: title,
      detail: detail || '',
      evidence: evidence || null
    });
  }

  /* --- helpers ------------------------------------------------------- */
  function safe(fn) { try { fn(); } catch (e) { console.warn('[audit] check failed:', e); } }

  function money(cents) {
    if (cents == null || isNaN(cents)) { return String(cents); }
    return '$' + (cents / 100).toFixed(2);
  }

  function parseMoney(text) {
    if (text == null) { return null; }
    var m = String(text).match(/[-+]?\d[\d.,]*/);
    if (!m) { return null; }
    var raw = m[0];
    if (raw.indexOf('.') > -1 && raw.indexOf(',') > -1) { raw = raw.replace(/,/g, ''); }
    else if (raw.indexOf(',') > -1) { raw = raw.replace(/,/g, ''); }
    var n = parseFloat(raw);
    return isNaN(n) ? null : n;
  }

  function uniq(arr) {
    var out = [], i;
    for (i = 0; i < arr.length; i++) { if (out.indexOf(arr[i]) === -1) { out.push(arr[i]); } }
    return out;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  function templateName() {
    if (window._template && window._template.name) { return window._template.name; }
    try { return window.ShopifyAnalytics.meta.page.pageType || ''; } catch (e) { return ''; }
  }

  function shortName(r) {
    try {
      var u = new URL(r.name);
      var p = u.pathname.split('/').filter(Boolean);
      return (p[p.length - 1] || u.hostname).slice(0, 40);
    } catch (e) { return String(r.name).slice(0, 40); }
  }

  function isThirdParty(host) {
    for (var i = 0; i < CFG.firstParty.length; i++) {
      if (host.indexOf(CFG.firstParty[i]) > -1) { return false; }
    }
    return true;
  }

  /* --- find product data already on the page ------------------------ */
  function collectProducts(obj, out, depth) {
    depth = depth || 0;
    if (!obj || typeof obj !== 'object' || depth > 4) { return; }
    if (Array.isArray(obj)) {
      for (var i = 0; i < obj.length; i++) { collectProducts(obj[i], out, depth + 1); }
      return;
    }
    if (Array.isArray(obj.variants) && obj.variants.length &&
        obj.variants[0] && typeof obj.variants[0] === 'object' && 'price' in obj.variants[0]) {
      out.push(obj);
    }
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] && typeof obj[k] === 'object') {
        collectProducts(obj[k], out, depth + 1);
      }
    }
  }

  function findEmbeddedProducts() {
    var found = [];
    var nodes = document.querySelectorAll('script[type="application/json"]');
    Array.prototype.forEach.call(nodes, function (node) {
      var data;
      try { data = JSON.parse(node.textContent); } catch (e) { return; }
      collectProducts(data, found, 0);
    });
    return found;
  }

  /* --- check: pricing integrity from page data ---------------------- */
  function checkProducts() {
    var products = findEmbeddedProducts();
    var seen = {};
    var checked = 0;

    products.forEach(function (p) {
      var key = p.id || p.handle || (p.variants || []).map(function (v) { return v.id; }).join('-');
      if (seen[key]) { return; }
      seen[key] = true;

      var title = p.title || p.handle || ('product ' + (p.id || ''));

      (p.variants || []).forEach(function (v) {
        checked++;
        var price = typeof v.price === 'number' ? v.price : parseInt(v.price, 10);
        var cmp = v.compare_at_price;
        if (typeof cmp === 'string') { cmp = parseInt(cmp, 10); }
        var vname = v.public_title || v.title || v.name || '';
        var label = title + (vname && vname !== 'Default Title' ? ' / ' + vname : '');

        if (isNaN(price) || price < 0) {
          flag('critical', 'Pricing', 'Invalid price', label + ' has price ' + v.price + '.', label);
        } else if (price === 0) {
          flag('warning', 'Pricing', 'Zero price', label + ' is set to $0.00. Confirm this is intentional.', label);
        }

        if (cmp != null && !isNaN(cmp)) {
          if (cmp < price) {
            flag('warning', 'Pricing', 'Compare-at below price',
              label + ': compare-at ' + money(cmp) + ' is lower than price ' + money(price) + '. The strike-through will read as a price increase.', label);
          } else if (cmp === price) {
            flag('warning', 'Pricing', 'Fake sale',
              label + ': compare-at equals price (' + money(price) + '). Shows a sale badge with no real discount.', label);
          }
        }

        if (v.available === true && v.inventory_management &&
            typeof v.inventory_quantity === 'number' && v.inventory_quantity <= 0 &&
            v.inventory_policy === 'deny') {
          flag('warning', 'Inventory', 'Sellable but out of stock',
            label + ' is marked available with 0 inventory and a deny policy.', label);
        }
      });
    });

    if (checked) {
      flag('info', 'Pricing', 'Variants checked', checked + ' variant price(s) read from page data.', null);
    }
  }

  /* --- check: prices as rendered in the DOM (fake / broken sales) --- */
  function checkDisplayedPrices() {
    var struck = document.querySelectorAll('del, s, .price__compare, [class*="compare-at"], [class*="compare_at"]');
    var count = 0;

    Array.prototype.forEach.call(struck, function (node) {
      if (count > 300) { return; }
      var cmp = parseMoney(node.textContent);
      if (cmp == null) { return; }

      var box = node.closest('.price, [class*="price"]') || node.parentElement;
      if (!box) { return; }

      var nums = [];
      Array.prototype.forEach.call(box.querySelectorAll('*'), function (el) {
        if (el === node || node.contains(el) || el.contains(node)) { return; }
        if (el.children.length) { return; }
        var n = parseMoney(el.textContent);
        if (n != null) { nums.push(n); }
      });
      if (!nums.length) { return; }

      var current = Math.min.apply(null, nums);
      count++;
      if (current >= cmp) {
        flag('warning', 'Pricing', 'Broken sale shown on page',
          'A struck price of $' + cmp.toFixed(2) + ' sits next to a current price of $' + current.toFixed(2) + '. The discount is zero or negative.',
          (box.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60));
      }
    });
  }

  /* --- product cards on the page ------------------------------------ */
  function getProductCards() {
    var anchors = document.querySelectorAll('a[href*="/products/"]');
    var cards = [], seen = [];
    Array.prototype.forEach.call(anchors, function (a) {
      var card = a.closest('li, .card, [class*="card"], [class*="product-card"], .grid__item') || a;
      if (seen.indexOf(card) > -1) { return; }
      if (!card.querySelector('img')) { return; }
      seen.push(card);
      cards.push(card);
    });
    return cards;
  }

  function checkCards(cards) {
    var noPrice = 0, noImg = 0;
    cards.slice(0, 200).forEach(function (card) {
      var hasMoney = /\$\s?\d/.test(card.textContent || '') ||
        !!card.querySelector('[class*="price"], [data-price]');
      if (!hasMoney) { noPrice++; }
      var img = card.querySelector('img');
      if (!img || (img.complete && img.naturalWidth === 0)) { noImg++; }
    });
    if (noPrice) { flag('warning', 'Collection', 'Cards without a price', noPrice + ' product card(s) render no price.', null); }
    if (noImg) { flag('warning', 'Collection', 'Cards without an image', noImg + ' product card(s) have a missing or broken image.', null); }
  }

  /* --- check: collection rendering ---------------------------------- */
  function checkCollection() {
    var t = templateName();
    var isCollection = t.indexOf('collection') > -1;
    var cards = getProductCards();

    if (isCollection && cards.length === 0) {
      var emptyMsg = /no products|nothing here|empty/i.test(document.body.innerText || '');
      flag(emptyMsg ? 'warning' : 'critical', 'Collection', 'Empty collection',
        'This collection page is rendering 0 products.', t);
      return;
    }

    if (isCollection) {
      flag('info', 'Collection', 'Products on page', cards.length + ' product card(s) rendered.', t);
    }
    if (cards.length) { checkCards(cards); }
  }

  /* --- check: broken media and missing alt -------------------------- */
  function checkImages() {
    var imgs = document.querySelectorAll('img');
    var broken = 0, noAlt = 0, total = 0;
    Array.prototype.forEach.call(imgs, function (img) {
      total++;
      if (img.complete && img.naturalWidth === 0 && img.src && img.getAttribute('loading') !== 'lazy') { broken++; }
      if (!img.alt && img.getAttribute('role') !== 'presentation') { noAlt++; }
    });
    if (broken) { flag('critical', 'Media', 'Broken images', broken + ' image(s) failed to load (naturalWidth 0).', null); }
    if (noAlt) { flag('info', 'SEO', 'Images missing alt text', noAlt + ' of ' + total + ' images have no alt attribute.', null); }
  }

  /* --- check: duplicate element IDs --------------------------------- */
  function checkDuplicateIds() {
    var map = {};
    Array.prototype.forEach.call(document.querySelectorAll('[id]'), function (el) {
      if (!el.id) { return; }
      map[el.id] = (map[el.id] || 0) + 1;
    });
    var dups = Object.keys(map).filter(function (id) { return map[id] > 1; });
    if (dups.length) {
      flag('warning', 'DOM', 'Duplicate element IDs',
        dups.length + ' id(s) appear more than once. This breaks anchor links and querySelector targeting.',
        dups.slice(0, 8).join(', '));
    }
  }

  /* --- check: quick SEO sanity -------------------------------------- */
  function checkSeo() {
    if (!document.title || !document.title.trim()) {
      flag('warning', 'SEO', 'Missing page title', 'The title tag is empty.', null);
    }
    var h1 = document.querySelectorAll('h1');
    if (h1.length === 0) { flag('warning', 'SEO', 'No H1', 'Page has no H1 heading.', null); }
    else if (h1.length > 1) { flag('info', 'SEO', 'Multiple H1s', h1.length + ' H1 headings found.', null); }
    if (!document.querySelector('link[rel="canonical"]')) { flag('info', 'SEO', 'No canonical tag', 'No canonical link found.', null); }
    if (!document.querySelector('meta[name="description"]')) { flag('info', 'SEO', 'No meta description', 'No meta description tag.', null); }
  }

  /* --- check: runtime JS errors ------------------------------------- */
  function checkRuntimeErrors() {
    if (!runtimeErrors.length) { return; }
    var sample = runtimeErrors.slice(0, 5).map(function (e) {
      return e.message + (e.where ? ' (' + e.where + ')' : '');
    }).join(' | ');
    flag('critical', 'JavaScript', 'Runtime errors', runtimeErrors.length + ' JS error(s) fired on this page.', sample);
  }

  /* --- check: cart and discount math (async) ------------------------ */
  function checkCart() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
    return fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart || !cart.items) { return; }
        if (cart.item_count === 0) {
          flag('info', 'Cart', 'Cart is empty', 'Add an item to audit discount and subscription math.', null);
          return;
        }

        var sumLine = 0;
        cart.items.forEach(function (item) {
          sumLine += item.line_price;

          if (item.price < 0) {
            flag('critical', 'Cart', 'Negative line price', item.title + ' has a negative price.', item.title);
          }
          if (item.line_price !== item.price * item.quantity) {
            flag('warning', 'Cart', 'Line price mismatch',
              item.title + ': line price ' + money(item.line_price) + ' is not price x qty (' + money(item.price * item.quantity) + ').', item.title);
          }
          if (typeof item.original_line_price === 'number' && typeof item.total_discount === 'number' &&
              item.original_line_price - item.total_discount !== item.line_price) {
            flag('warning', 'Discounts', 'Line discount math off',
              item.title + ': original ' + money(item.original_line_price) + ' minus discount ' + money(item.total_discount) + ' does not equal line price ' + money(item.line_price) + '.', item.title);
          }
          if (item.selling_plan_allocation && typeof item.selling_plan_allocation.price === 'number' &&
              item.selling_plan_allocation.price !== item.price) {
            flag('warning', 'Subscription', 'Selling plan price not applied',
              item.title + ': selling plan price ' + money(item.selling_plan_allocation.price) + ' does not match the charged price ' + money(item.price) + '.', item.title);
          }
        });

        if (typeof cart.items_subtotal_price === 'number' && cart.items_subtotal_price !== sumLine) {
          flag('warning', 'Cart', 'Subtotal mismatch',
            'items_subtotal_price ' + money(cart.items_subtotal_price) + ' does not equal the sum of line prices ' + money(sumLine) + '.', null);
        }
        if (typeof cart.original_total_price === 'number' && typeof cart.total_discount === 'number' &&
            cart.original_total_price - cart.total_discount !== cart.total_price) {
          flag('warning', 'Discounts', 'Cart discount math off',
            'original total ' + money(cart.original_total_price) + ' minus total discount ' + money(cart.total_discount) + ' does not equal total ' + money(cart.total_price) + '.', null);
        }
        if (cart.total_price < 0) {
          flag('critical', 'Cart', 'Negative cart total', 'Cart total is below zero.', null);
        }
        (cart.cart_level_discount_applications || []).forEach(function (d) {
          if (d.total_allocated_amount === 0) {
            flag('info', 'Discounts', 'Discount applied with no value',
              (d.title || d.key || 'A discount') + ' is applied but allocates $0.00.', null);
          }
        });

        flag('info', 'Cart', 'Cart audited', cart.item_count + ' item(s), total ' + money(cart.total_price) + '.', null);
      })
      .catch(function () {
        flag('info', 'Cart', 'Cart not readable', 'Could not fetch /cart.js.', null);
      });
  }

  /* --- check: performance (refactored from the original) ------------ */
  function checkPerformance() {
    if (!window.performance || !performance.getEntriesByType) { return; }
    var res = performance.getEntriesByType('resource');
    var nav = performance.getEntriesByType('navigation')[0];
    var paint = performance.getEntriesByType('paint');
    var fcp = paint.filter(function (p) { return p.name === 'first-contentful-paint'; })[0];

    var byDomain = {};
    var slow = [], blocking = [], thirdTime = 0, firstTime = 0;

    res.forEach(function (r) {
      var host = 'unknown';
      try { host = new URL(r.name).hostname; } catch (e) {}
      var tp = isThirdParty(host);
      if (tp) { thirdTime += r.duration; } else { firstTime += r.duration; }

      if (!byDomain[host]) { byDomain[host] = { ms: 0, kb: 0, n: 0, tp: tp }; }
      byDomain[host].ms += r.duration;
      byDomain[host].kb += (r.transferSize || 0) / 1024;
      byDomain[host].n++;

      if (r.duration >= CFG.slowMs) { slow.push(r); }
      if ((r.initiatorType === 'script' || r.initiatorType === 'link') && r.startTime < 500 && r.duration > 100) {
        blocking.push(r);
      }
    });

    var tpPct = (thirdTime + firstTime) > 0 ? Math.round(thirdTime / (thirdTime + firstTime) * 100) : 0;
    var load = nav ? Math.round(nav.loadEventEnd) : null;
    var fcpMs = fcp ? Math.round(fcp.startTime) : null;
    var critSlow = slow.filter(function (r) { return r.duration > CFG.criticalMs; });

    if (load && load > 3000) {
      flag('warning', 'Performance', 'Slow page load', 'Page load finished at ' + load + 'ms (aim under 3000ms).', null);
    }
    if (fcpMs && fcpMs > 1800) {
      flag('warning', 'Performance', 'Slow first paint', 'First Contentful Paint at ' + fcpMs + 'ms (aim under 1800ms).', null);
    }
    if (critSlow.length) {
      var names = uniq(critSlow.map(shortName)).slice(0, 4).join(', ');
      flag('warning', 'Performance', 'Very slow resources',
        critSlow.length + ' resource(s) took over ' + CFG.criticalMs + 'ms. Worst: ' + names + '.', null);
    }
    if (tpPct > 50) {
      flag('warning', 'Performance', 'Third-party heavy', tpPct + '% of resource time is third-party apps and pixels.', null);
    }
    if (blocking.length > 3) {
      flag('info', 'Performance', 'Render-blocking', blocking.length + ' early script/style resource(s) may delay first paint.', null);
    }

    console.groupCollapsed('%cPerformance detail', 'font-weight:bold;color:#4aa3ff');
    console.log('Load ' + (load || '?') + 'ms   FCP ' + (fcpMs || '?') + 'ms   third-party ' + tpPct + '%   resources ' + res.length);
    var domainTable = Object.keys(byDomain).map(function (h) {
      return {
        Domain: h,
        ms: Math.round(byDomain[h].ms),
        KB: Math.round(byDomain[h].kb),
        Requests: byDomain[h].n,
        ThirdParty: byDomain[h].tp ? 'yes' : 'no'
      };
    }).sort(function (a, b) { return b.ms - a.ms; }).slice(0, 12);
    if (console.table) { console.table(domainTable); }
    console.groupEnd();
  }

  /* --- console report ----------------------------------------------- */
  function reportConsole() {
    var c = { critical: 0, warning: 0, info: 0 };
    issues.forEach(function (i) { if (c[i.severity] != null) { c[i.severity]++; } });

    console.log('%cOGPB Storefront Audit', 'font-size:14px;font-weight:bold;color:#4aa3ff');
    console.log(c.critical + ' critical, ' + c.warning + ' warning, ' + c.info + ' info');

    ['critical', 'warning', 'info'].forEach(function (sev) {
      var rows = issues.filter(function (i) { return i.severity === sev; });
      if (!rows.length) { return; }
      console.groupCollapsed('%c' + sev.toUpperCase() + ' (' + rows.length + ')',
        'color:' + SEVMETA[sev].color + ';font-weight:bold');
      rows.forEach(function (r) {
        console.log('[' + r.area + '] ' + r.title +
          (r.detail ? ' - ' + r.detail : '') +
          (r.evidence ? '   // ' + r.evidence : ''));
      });
      console.groupEnd();
    });
  }

  /* --- floating panel ----------------------------------------------- */
  function injectStyles() {
    if (document.getElementById('__ogpb_audit_css')) { return; }
    var css = '' +
      '#__ogpb_audit{position:fixed;right:16px;bottom:16px;width:360px;max-width:calc(100vw - 32px);' +
      'max-height:72vh;z-index:2147483647;display:flex;flex-direction:column;background:#14151a;' +
      'border:1px solid #2a2c34;border-radius:10px;box-shadow:0 12px 40px rgba(0,0,0,.45);' +
      'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
      'font-size:13px;line-height:1.45;color:#e8e8ea;}' +
      '#__ogpb_audit *{box-sizing:border-box;}' +
      '#__ogpb_audit .ah{display:flex;align-items:center;gap:8px;padding:10px 12px;background:#1b1d24;' +
      'border-bottom:1px solid #2a2c34;border-radius:10px 10px 0 0;cursor:pointer;user-select:none;}' +
      '#__ogpb_audit .ah .dot{width:9px;height:9px;border-radius:50%;flex:0 0 auto;}' +
      '#__ogpb_audit .ah .verdict{font-weight:700;flex:1 1 auto;}' +
      '#__ogpb_audit .chip{font-size:11px;font-weight:700;padding:2px 7px;border-radius:20px;color:#fff;}' +
      '#__ogpb_audit .btn{background:transparent;border:1px solid #2a2c34;color:#b9bbc4;border-radius:6px;' +
      'padding:3px 8px;font-size:11px;cursor:pointer;}' +
      '#__ogpb_audit .btn:hover{color:#fff;border-color:#3a3d48;}' +
      '#__ogpb_audit .ab{overflow-y:auto;padding:8px;display:flex;flex-direction:column;gap:6px;}' +
      '#__ogpb_audit.collapsed .ab{display:none;}' +
      '#__ogpb_audit .row{padding:8px 9px;background:#181a20;border-left:3px solid #444;border-radius:6px;}' +
      '#__ogpb_audit .row .tag{display:inline-block;font-size:10px;letter-spacing:.04em;text-transform:uppercase;' +
      'color:#8b8e98;margin-right:6px;}' +
      '#__ogpb_audit .row .ttl{font-weight:600;}' +
      '#__ogpb_audit .row .det{color:#b9bbc4;margin-top:2px;}' +
      '#__ogpb_audit .row .ev{color:#7f828c;margin-top:3px;font-family:ui-monospace,Menlo,Consolas,monospace;' +
      'font-size:11px;word-break:break-word;}' +
      '#__ogpb_audit .ok{padding:14px;text-align:center;color:#3ddc84;}';
    var s = document.createElement('style');
    s.id = '__ogpb_audit_css';
    s.textContent = css;
    document.head.appendChild(s);
  }

  function render() {
    injectStyles();
    var existing = document.getElementById('__ogpb_audit');
    var collapsed = existing ? existing.classList.contains('collapsed') : null;
    if (existing) { existing.remove(); }

    var counts = { critical: 0, warning: 0, info: 0 };
    issues.forEach(function (i) { if (counts[i.severity] != null) { counts[i.severity]++; } });

    var verdictColor = counts.critical ? SEVMETA.critical.color
      : counts.warning ? SEVMETA.warning.color : '#3ddc84';
    var verdictText = counts.critical ? (counts.critical + ' critical issue' + (counts.critical > 1 ? 's' : ''))
      : counts.warning ? (counts.warning + ' warning' + (counts.warning > 1 ? 's' : ''))
        : 'No blocking issues';

    if (collapsed === null) { collapsed = !(counts.critical || counts.warning); }

    var order = { critical: 0, warning: 1, info: 2 };
    var sorted = issues.slice().sort(function (a, b) { return order[a.severity] - order[b.severity]; });

    var rows = '';
    var blocking = sorted.filter(function (i) { return i.severity !== 'info'; });
    if (!blocking.length) {
      rows += '<div class="ok">No critical or pricing issues found on this page.</div>';
    }
    sorted.forEach(function (i) {
      var color = SEVMETA[i.severity] ? SEVMETA[i.severity].color : '#444';
      rows += '<div class="row" style="border-left-color:' + color + '">' +
        '<div><span class="tag">' + esc(i.area) + '</span><span class="ttl">' + esc(i.title) + '</span></div>' +
        (i.detail ? '<div class="det">' + esc(i.detail) + '</div>' : '') +
        (i.evidence ? '<div class="ev">' + esc(i.evidence) + '</div>' : '') +
        '</div>';
    });

    var chip = function (sev, n) {
      if (!n) { return ''; }
      return '<span class="chip" style="background:' + SEVMETA[sev].color + '">' + n + '</span>';
    };

    var root = document.createElement('div');
    root.id = '__ogpb_audit';
    if (collapsed) { root.className = 'collapsed'; }
    root.innerHTML =
      '<div class="ah">' +
        '<span class="dot" style="background:' + verdictColor + '"></span>' +
        '<span class="verdict">' + esc(verdictText) + '</span>' +
        chip('critical', counts.critical) + chip('warning', counts.warning) + chip('info', counts.info) +
        '<button class="btn js-rerun" title="Re-run">Re-run</button>' +
        '<button class="btn js-close" title="Close">X</button>' +
      '</div>' +
      '<div class="ab">' + rows + '</div>';

    document.body.appendChild(root);

    root.querySelector('.ah').addEventListener('click', function (e) {
      if (e.target.closest('.btn')) { return; }
      root.classList.toggle('collapsed');
    });
    root.querySelector('.js-rerun').addEventListener('click', function (e) {
      e.stopPropagation();
      run();
    });
    root.querySelector('.js-close').addEventListener('click', function (e) {
      e.stopPropagation();
      root.remove();
    });
  }

  /* --- orchestration ------------------------------------------------ */
  function finish() {
    render();
    reportConsole();
  }

  function run() {
    issues = [];
    safe(checkRuntimeErrors);
    safe(checkProducts);
    safe(checkDisplayedPrices);
    safe(checkCollection);
    safe(checkImages);
    safe(checkDuplicateIds);
    safe(checkSeo);
    safe(checkPerformance);
    render();
    checkCart().then(finish, finish);
  }

  window.OGPBAudit = { run: run, issues: function () { return issues.slice(); } };

  if (document.readyState === 'complete') {
    setTimeout(run, 600);
  } else {
    window.addEventListener('load', function () { setTimeout(run, 600); });
  }
})();
